import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { AlertCircle, Building01, Calendar, XClose } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { Key } from "react-aria-components";
import z from "zod";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { DrawerHeader } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { Illustration } from "@/components/shared-assets/illustrations";
import { SubOrganizationDeleteControl } from "@/features/sub-organizations/SubOrganizationDeleteControl";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { organizationQueryOptions } from "@/services/organization";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum([
	"details",
	"users",
	"data-access",
	"activity",
]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
	organizationId: z.string().optional(),
	subOrganizationId: z.string().optional(),
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const statusColors = {
	Active: "success",
	Suspended: "error",
	Archived: "gray",
	Retired: "warning",
} as const;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			subOrgId: z.string().parse(params.subOrgId),
		}),
	},
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId",
	});
	const [isOpen, setIsOpen] = useState(false);

	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	const subOrganizationByIdQuery = useQuery(
		organizationQueryOptions.subOrganizations.detail(
			params.orgId,
			params.subOrgId,
		),
	);
	const subOrganizationById = subOrganizationByIdQuery.data;

	useEffect(() => {
		setIsOpen(true);
	}, []);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: "/org-admin/$orgId/sub-organizations",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = subOrganizationById?.name
		? subOrganizationById.name
		: t("org-admin.sub-organizations.drawer.title");
	const subOrganizationLabel = t("subOrganization.entityLabel");
	const subOrganizationName = subOrganizationById?.name ?? subOrganizationLabel;
	const tabs = [
		{
			label: t("org-admin.sub-organizations.drawer.tabs.details"),
			id: "details",
		},
		{
			label: t("org-admin.sub-organizations.drawer.tabs.users"),
			id: "users",
		},
		{
			label: t("org-admin.sub-organizations.drawer.tabs.dataAccess"),
			id: "data-access",
		},
		{
			label: t("org-admin.sub-organizations.drawer.tabs.activity"),
			id: "activity",
		},
	];
	const metadata = [
		...(subOrganizationById?.created
			? [
					{
						id: "created",
						label: createdMetadataLabel(t, subOrganizationById.created),
						icon: Calendar,
					},
				]
			: []),
		...(subOrganizationById?.lastUpdated
			? [
					{
						id: "updated",
						label: updatedMetadataLabel(t, subOrganizationById.lastUpdated),
						icon: Calendar,
					},
				]
			: []),
	];

	const goToTab = (tabId: string) => {
		navigate({
			to: `/org-admin/$orgId/sub-organizations/$subOrgId/${tabId}`,
			params: {
				orgId: params.orgId,
				subOrgId: params.subOrgId,
			},
			search: { mode: "view" },
		});
	};

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="w-[570px]"
			backdropPreset="transparent"
			dialogClassName="gap-0 overflow-hidden"
		>
			{({ close }) => (
				<div className="flex h-full min-h-0 w-full flex-col">
					<DrawerHeader
						type="default"
						title={title}
						featuredIcon={Building01}
						showSpacer={false}
						metadata={metadata}
						overflowControl={
							<>
								<SubOrganizationDeleteControl
									organizationId={params.orgId}
									subOrganizationId={params.subOrgId}
									name={subOrganizationName}
									label={subOrganizationLabel}
									onDeleted={() => {
										close();
										handleClose();
									}}
								/>
								<IconButton
									aria-label={t("common.actions.close")}
									hierarchy="secondary"
									size="sm"
									shape="square"
									icon={XClose}
									onPress={() => {
										close();
										handleClose();
									}}
								/>
							</>
						}
					/>
					{subOrganizationByIdQuery.isLoading ? (
						<div className="flex min-h-0 flex-1 items-center justify-center bg-tertiary">
							<LoadingIndicator type="line-simple" size="xl" supportingText />
						</div>
					) : subOrganizationByIdQuery.isError ? (
						<div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-tertiary">
							<Illustration type="cloud" size="sm">
								<div className="bg-error-500 rounded-full p-3">
									<AlertCircle className="size-6" />
								</div>
							</Illustration>
							<div className="flex flex-col items-center justify-center pt-4">
								<p className="text-gray-900 font-semibold text-md">
									{t("admin.organizations.errors.errorLoadingPage")}
								</p>
								<div className="w-[60%]">
									<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
										{t("admin.organizations.errors.errorLoadingContent")}
									</p>
								</div>
							</div>
						</div>
					) : (
						<Drawer.ScrollBody showPlaceholder={false}>
							<NativeSelect
								aria-label={t("org-admin.sub-organizations.drawer.tabs.details")}
								value={currentTab}
								onChange={(event) => goToTab(event.target.value)}
								options={tabs.map((tab) => ({
									label: tab.label,
									value: tab.id,
								}))}
								className="mx-6 mt-3 w-80 md:hidden"
							/>
							<div className="sticky top-0 z-10 flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
								<Tabs
									selectedKey={currentTab}
									onSelectionChange={(key: Key) => goToTab(String(key))}
									className="h-full w-full"
								>
									<TabList
										type="underline"
										size="sm"
										items={tabs}
										className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
									>
										{(item) => (
											<Tab
												id={item.id}
												label={item.label}
												className="h-full items-end -mb-px"
											/>
										)}
									</TabList>
								</Tabs>
							</div>
							<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
								<TabContentWrapper tabKey={currentTab}>
									<Outlet />
								</TabContentWrapper>
							</div>
						</Drawer.ScrollBody>
					)}
				</div>
			)}
		</Drawer>
	);
}
